import { convertDate, convertDateUTC } from 'utils/convertDate';
import { NameBlock } from '../../../../../../components/Table/renderers';
import {
  CourseProgressLogType,
  CourseProgressStatus,
  ICourseProgressDAO,
  ICourseProgressLog,
} from 'types/domain/permissions';
import { useCallback, useMemo } from 'react';
import { ICourseDAO, ICourseVariableDAO } from 'types/domain/course';
import { VariableTypeIcon } from '../../Constants/variable';
import { actionVariableNode } from 'app/components/ActionEdit/Variable';
import {
  BoltIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  CheckIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef, Row } from '@tanstack/react-table';
import { DeviceType, IOrgUserVariableDAO, IOrganisationUser } from 'types/domain/user';
import { convertTimeAgo } from 'utils/timeAgo';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { millisToMinutesAndSeconds } from 'utils/convertTime';
import { selectModuleCached } from 'core/selectors/product/module';
import { IModuleDAO } from 'types/domain/module';
import { getImageFromFiles } from 'core/selectors/product/scene/scenePreview';
import { Resolutions } from 'types/domain/image';
import { BsStopwatch } from 'react-icons/bs';
import { ReportType, selectReportRow } from 'core/slices/report';
import { CourseVariableTotal } from '../../Variables';
import { EntityTypes } from 'constants/entities';
import TextEditor, { TextType } from 'app/components/TextEditor';
import { ActionType, IActionBasic } from 'types/domain/action';
import { selectAction } from 'core/selectors/product/action/action';
import { ActionPreviewImage } from 'app/components/ActionEdit/PreviewImage';
import { ISceneDAO } from 'types/domain/scene';
import { selectScene } from 'core/selectors/product/scene/scene';
import { ISessionAttempt, SessionAttemptStatus } from 'types/domain/session';

import { IEmail } from 'types/domain/notifications';
import { LatestCertificate } from 'app/pages/Manage/Certificates/columns';

export const columns = (course: ICourseDAO, portrait, isDefault, edit) => {
  if (portrait)
    return [
      {
        id: 'firstName',
        header: 'Name',
        cell: data => (
          <div className="flex flex-col justify-center p-2">
            <NameBlock
              user={data.cell.row.original.organisationUser}
              group={data.cell.row.original.lastGroupUser?.group}
            />
            {data.cell.row.original.courseOrgUserVariable ? (
              <div className="mt-2 ml-8">
                <CourseOutcome courseProgress={data.cell.row.original} />
              </div>
            ) : (
              <div className="mt-2 ml-2">
                <Latest courseProgress={data.cell.row.original} />
              </div>
            )}
          </div>
        ),
      },
    ];

  return [
    {
      id: 'select',
      header: 'Select',
      cell: data => <Select courseProgress={data.cell.row.original} />,
    },
    {
      id: 'firstName',
      header: 'Name',
      width: 50,
      cell: data => (
        <NameBlock
          edit={edit}
          user={data.cell.row.original.organisationUser}
          group={data.cell.row.original.lastGroupUser?.group}
        />
      ),
    },
    {
      id: 'started',
      header: 'Status',
      cell: data => <Status courseProgress={data.row.original} />,
    },

    // {
    //   id: 'latest',
    //   header: 'LATEST',
    //   cell: data => <Latest courseProgress={data.cell.row.original} />,
    // },

    {
      id: 'outcomes',
      header: 'Progress',
      cell: data => <Outcomes courseVariables={course.courseVariables} courseProgress={data.cell.row.original} />,
    },

    // {
    //   id: 'remove',
    //   header: 'REMOVE',
    //   cell: data => <Delete courseProgress={data.cell.row.original} />,
    // },
  ];
};

export const variableColumns = (course: ICourseDAO, portrait) => {
  if (portrait)
    return [
      {
        id: 'firstName',
        header: 'Name',
        cell: data => (
          <div className="flex flex-col text-gray-800 justify-center p-2">
            <NameBlock
              user={data.cell.row.original.organisationUser}
              group={data.cell.row.original.lastGroupUser?.group}
            />
            {data.cell.row.original.courseOrgUserVariable ? (
              <div className="mt-2 ml-8">
                <Outcomes
                  courseVariables={course.courseVariables}
                  courseProgress={data.cell.row.original}
                  showVariables={true}
                />
              </div>
            ) : (
              <div className="mt-2 ml-2">
                <Latest courseProgress={data.cell.row.original} />
              </div>
            )}
          </div>
        ),
      },
    ];

  return [
    {
      id: 'firstName',
      header: 'Name',
      width: 50,
      cell: data => (
        <NameBlock user={data.cell.row.original.organisationUser} group={data.cell.row.original.lastGroupUser?.group} />
      ),
    },
    ...(course
      ? course?.courseVariables.map((cv: ICourseVariableDAO) => {
          //@ts-ignore
          return {
            id: cv.id.toString(),
            header: <CourseVariableTotal courseVariable={cv} />,
            width: 50,
            accessorFn: data => data.id,
            cell: data => <CourseVariable courseProgress={data.cell.row.original} courseVariable={cv} />,
          } as ColumnDef<ICourseProgressDAO, any>;
        })
      : []),

    // {
    //   id: 'remove',
    //   header: 'REMOVE',
    //   cell: data => <Delete courseProgress={data.cell.row.original} />,
    // },
  ];
};

export const courseProgressColumns = () => {
  return [
    {
      id: 'date',
      header: 'Date',
      cell: data => {
        const date = data.row.original.modified?.date || data.row.original.created?.date;
        return (
          <>
            {convertDateUTC(date)?.toDateString()}
            {', '}
            {convertDateUTC(date).toLocaleTimeString('en-GB')}
          </>
        );
      },
    },
    {
      id: 'activity',
      header: 'Activity',
      cell: data => <CourseProgressLog log={data.row.original} />,
    },
    {
      id: 'result',
      header: 'Result',
      cell: data => <CourseProgressResult log={data.row.original} />,
    },
  ];
};

function CourseProgressLog({ log }: { log: ICourseProgressLog }) {
  switch (log.type) {
    case CourseProgressLogType.SESSION_ATTEMPT:
      return <LatestModule sessionAttempt={log.sessionAttempt} />;
    case CourseProgressLogType.ORG_USER_VARIABLE:
      return <VariableRender organisationUserVariable={log.orgUserVariable} />;
    case CourseProgressLogType.EMAIL:
      return <LatestEmail email={log.email} />;
    case CourseProgressLogType.ORG_USER_CERTIFICATE:
      return <LatestCertificate certificate={log.orgUserCertificate} />;
  }
}
function CourseProgressResult({ log }: { log: ICourseProgressLog }) {
  switch (log.type) {
    case CourseProgressLogType.SESSION_ATTEMPT:
      return <VariableRender organisationUserVariable={log.orgUserVariable} result={true} />;
    case CourseProgressLogType.ORG_USER_VARIABLE:
      return <VariableRender organisationUserVariable={log.orgUserVariable} result={true} />;
  }
}

export function Status({ courseProgress }: { courseProgress: ICourseProgressDAO }) {
  const status = () => {
    switch (courseProgress.status) {
      case CourseProgressStatus.NOT_STARTED:
        return (
          <div className="flex items-center">
            <div className={`rounded-full h-4 w-4 bg-gray-400 mr-2 text-sm`} /> Not started
          </div>
        );
      case CourseProgressStatus.DORMANT:
        return (
          <div className="flex items-center">
            <div className={`rounded-full h-4 w-4 bg-yellow-400 mr-2 text-sm`} /> Started
          </div>
        );
      case CourseProgressStatus.ACTIVE:
        return (
          <div className="flex items-center">
            <div className={`rounded-full h-4 w-4 bg-orange-400 mr-2 text-sm`} /> Online
          </div>
        );
      case CourseProgressStatus.COMPLETE:
        return (
          <div className="flex items-center">
            <div className={`rounded-full h-4 w-4 bg-green-400 mr-2 text-sm`} /> Complete
          </div>
        );
      case CourseProgressStatus.RESTARTED:
        return (
          <div className="flex items-center">
            <div className={`rounded-full h-4 w-4 bg-blue-300 mr-2 text-sm`} /> Restarted
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col">
      {status()}
      {courseProgress.created?.date && CalendarDateCell(courseProgress.created.date)}
    </div>
  );
}

export function CourseOutcome({ courseProgress }: { courseProgress: ICourseProgressDAO }) {
  const indicator = useMemo(() => {
    if (!courseProgress.courseOrgUserVariable) return null;

    return (
      <div className="flex flex-col w-full text-gray-800">
        <div className="flex text-brand items-center  mb-1">
          <CheckBadgeIcon className="w-5 h-5 mr-2" />
          <span className="dark:text-white">Completed</span>
        </div>

        <div className="text-sm dark:text-gray-300 flex flex-col ml-1">
          <span className="flex items-center">
            <CalendarDaysIcon className="w-3 h-3 mr-3" />
            {convertDateUTC(courseProgress.courseOrgUserVariable.created.date).toDateString()}
            {', '}
            {convertDateUTC(courseProgress.courseOrgUserVariable.created.date).toLocaleTimeString('en-GB')}
          </span>

          {courseProgress.statsProgress ? (
            <span className="flex items-center">
              <BoltIcon className="w-3 h-3 mr-3" /> {`Progress: ${(courseProgress.statsProgress * 100).toFixed(0)}%`}
            </span>
          ) : null}
          {courseProgress.statsTime ? (
            <span className="flex items-center">
              <BsStopwatch className="w-3 h-3 mr-3" /> {`${millisToMinutesAndSeconds(courseProgress.statsTime)}`}
            </span>
          ) : null}
        </div>
      </div>
    );
  }, [courseProgress]);

  return <div className="flex text-md text-gray-400 flex-col">{indicator}</div>;
}

export function Outcomes({
  courseProgress,
  courseVariables,
  showVariables = false,
}: {
  courseProgress: ICourseProgressDAO;
  courseVariables: ICourseVariableDAO[];
  showVariables?: boolean;
}) {
  const variables = useMemo(() => {
    if (!courseVariables || !courseProgress) return null;

    return courseVariables
      .filter(cv => !!courseProgress.organisationUserVariables[cv.variableId])
      .map((cv, i) => <CourseVariable courseProgress={courseProgress} courseVariable={cv} showLabel={true} />);
  }, [courseProgress, courseVariables]);

  return (
    <div className="flex flex-col text-sm text-gray-100">
      {courseProgress.courseOrgUserVariable ? (
        <CourseOutcome courseProgress={courseProgress} />
      ) : (
        <Latest courseProgress={courseProgress} />
      )}
    </div>
  );
}

export function CalendarDateCell(date: string) {
  const d = convertDateUTC(date);

  if (d instanceof Date)
    return (
      <span className="flex items-center text-sm ml-5 text-gray-800">
        <CalendarDaysIcon className="w-3 h-3 mr-1" />
        {d.toDateString()}
        {', '}
        {d.toLocaleTimeString('en-GB')}
      </span>
    );
}

export function CourseVariable({ courseProgress, courseVariable, showLabel = false }) {
  if (!courseProgress?.organisationUserVariables[courseVariable?.variableId]) return <></>;

  return (
    <div className="flex flex-col text-sm">
      <span className=" flex items-center my-1">
        {showLabel && (
          <>
            {VariableTypeIcon(courseVariable.variable?.type, 'w-4 h-4')}
            <span className="ml-2 mr-4">{courseVariable.variable?.name}</span>
          </>
        )}

        {actionVariableNode(
          courseVariable.variable,
          courseProgress.organisationUserVariables[courseVariable.variableId],
          'w-4 h-4',
        )}
      </span>
      {courseProgress.organisationUserVariables[courseVariable.variableId].textId && (
        <TextEditor
          readOnly={true}
          parentId={courseProgress.organisationUserVariables[courseVariable.variableId].id}
          parentType={EntityTypes.ORG_USER_VARIABLE}
          textId={courseProgress.organisationUserVariables[courseVariable.variableId].textId}
        />
      )}
      {/* <span className='ml-5 text-xs'> {convertDate(courseProgress.modified?.date).toDateString()}</span> */}
    </div>
  );
}

export function VariableRender({
  organisationUserVariable,
  result,
}: {
  organisationUserVariable: IOrgUserVariableDAO;
  result?: boolean;
}) {
  if (organisationUserVariable)
    return (
      <div className="flex flex-col text-sm">
        <span className=" flex items-center my-1">
          {result ? (
            <>
              {actionVariableNode(organisationUserVariable.variable, organisationUserVariable, 'w-4 h-4')}

              {organisationUserVariable.textId && (
                <TextEditor
                  readOnly={true}
                  parentId={organisationUserVariable.id}
                  parentType={EntityTypes.ORG_USER_VARIABLE}
                  textId={organisationUserVariable.textId}
                  type={TextType.HTML}
                />
              )}
            </>
          ) : (
            <>
              {VariableTypeIcon(organisationUserVariable.variable?.type, 'w-6 h-6')}
              <span className="ml-2 mr-4">
                {organisationUserVariable.variable?.textId && (
                  <TextEditor
                    readOnly={true}
                    parentId={organisationUserVariable.variable.id}
                    parentType={EntityTypes.VARIABLE}
                    textId={organisationUserVariable.variable.textId}
                    type={TextType.SHORT_TEXT}
                  />
                )}
              </span>
            </>
          )}
        </span>

        {/* <span className="ml-5 text-xs">
        {convertDate(organisationUserVariable.modified?.date || organisationUserVariable.created?.date)?.toDateString()}
      </span> */}
      </div>
    );
}

function Action({ actionId }: { actionId: number }) {
  //@ts-ignore
  const action: IActionBasic = useAppSelector(state => selectAction(state, actionId));
  const scene: ISceneDAO = useAppSelector(state =>
    //@ts-ignore
    action && action.sceneId ? selectScene(state, action.sceneId) : null,
  );
  if (action)
    return (
      <div className="flex-col flex text-slate-800 w-40 h-24 overflow-hidden">
        {scene ? (
          <div className="relative flex flex-col w-full h-full z-0 justify-center items-center ">
            <ActionPreviewImage
              id={actionId}
              className={`rounded ${
                action.type === ActionType.SIMPLE ? 'brightness-50' : ''
              }  w-full overflow-hidden h-full absolute z-0`}
            />

            {action.type !== ActionType.INTERACTIVE ? (
              <TextEditor
                readOnly={true}
                textId={action.textId}
                className={'overflow-hidden absolute z-1 text-sm w-full h-full bg-black/40'}
                parentId={action.id}
                parentType={EntityTypes.ACTION}
              />
            ) : null}
          </div>
        ) : (
          <TextEditor
            readOnly={true}
            textId={action.textId}
            className={' overflow-hidden text-sm w-full h-full'}
            parentId={action.id}
            parentType={EntityTypes.ACTION}
          />
        )}
      </div>
    );
}

export function Latest({ courseProgress }: { courseProgress: ICourseProgressDAO }) {
  if (!courseProgress.sessionAttempt && !courseProgress.lastEmail) return null;
  if (courseProgress.sessionAttempt && !courseProgress.lastEmail)
    return <LatestModule sessionAttempt={courseProgress.sessionAttempt} />;
  if (!courseProgress.sessionAttempt && courseProgress.lastEmail)
    return <LatestEmail email={courseProgress.lastEmail} />;

  if (courseProgress.sessionAttempt.created?.date > courseProgress.lastEmail.created?.date)
    return <LatestEmail email={courseProgress.lastEmail} />;
  else return <LatestModule sessionAttempt={courseProgress.sessionAttempt} />;
}

export function LatestEmail({ email }: { email: IEmail }) {
  return (
    <div className="flex text-gray-800">
      <EnvelopeIcon className="w-8 h-8" />
      <div className="flex flex-col mx-2">
        <span className="truncate  w-40">{email.subject}</span>
        <span className="text-sm ">{convertTimeAgo(convertDate(email.created?.date))}</span>
      </div>
    </div>
  );
}

export function LatestModule({ sessionAttempt, showDate }: { sessionAttempt: ISessionAttempt; showDate?: boolean }) {
  const module: IModuleDAO = useAppSelector(state =>
    //@ts-ignore
    selectModuleCached(state, sessionAttempt?.moduleId || courseProgress.moduleId),
  );
  const img = useMemo(() => getImageFromFiles(module?.image?.files, Resolutions.SMALL_512), [module?.image?.files]);
  if (!module) return null;

  const status = () => {
    if (!sessionAttempt) return ['', ''];

    switch (sessionAttempt.status) {
      case SessionAttemptStatus.ACTIVE:
        return ['bg-orange-500', 'Active'];
      case SessionAttemptStatus.COMPLETE:
        return ['bg-green-500', 'Complete'];
      case SessionAttemptStatus.NEW:
        return ['bg-yellow-500', 'Started'];
      default:
        return ['bg-grey-500', 'Not started'];
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex">
        {img && (
          <div
            id={'image'}
            style={{
              backgroundImage: img ? `url(${img})` : null,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              transform: module?.image?.rotation ? `rotate(${module.image.rotation}deg)` : null,
            }}
            className="w-10 h-10 rounded-lg z-0 pointer-events-none flex justify-center items-start"
          />
        )}

        <div className="flex flex-col ml-2">
          <span className="text-gray-800 dark:text-white truncate w-40">{module.name}</span>

          {sessionAttempt && (
            <>
              <div className="flex justify-start items-center text-gray-800">
                <div className={`rounded-full h-2 w-2 mr-2 ${status()[0]}`} />
                <span className="text-gray-800">{status()[1]}</span>
              </div>
              {/* <span className="text-sm text-gray-400">{convertTimeAgo(convertDate(sessionAttempt.created?.date))}</span> */}
              {showDate && sessionAttempt.created && (
                <span className="flex items-center text-gray-800">
                  <CalendarDaysIcon className="w-3 h-3 mr-1" />
                  {convertDateUTC(sessionAttempt.created?.date).toDateString()}
                </span>
              )}

              {!!sessionAttempt.statsProgress && (
                <span className="text-xs text-gray-800">{(sessionAttempt.statsProgress * 100).toFixed(0)}%</span>
              )}
              {!!sessionAttempt.statsTime ? (
                <span className="flex items-center text-sm text-gray-800">
                  <BsStopwatch className="w-3 h-3 mr-1" />
                  {`${millisToMinutesAndSeconds(sessionAttempt.statsTime)}`}
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function SignIn({ row }: { row: Row<ICourseProgressDAO> }) {
  const user = row.original.organisationUser;

  if (!user || row.depth > 0) return null;

  return getLastSignin(user);
}

export function getLastSignin(user: IOrganisationUser) {
  if (!user.signin)
    return (
      <div className="inline-flex rounded-md bg-slate-100 dark:bg-slate-800 dark:text-gray-300 px-5 py-3 text-sm font-semibold leading-5 text-gray-800">
        Inactive
      </div>
    );

  return (
    <div className="inline-flex dark:text-gray-300 text-gray-800  rounded-md px-4 py-1 text-xs leading-5 ">
      {user.signin && (
        <span className="inline-block  pt-1 h-7 w-7 mr-2">
          <span className="h-6 w-6 pt-2">
            {user.signin.lastLoginSignin?.deviceType === DeviceType.DESKTOP && <ComputerDesktopIcon />}
            {user.signin.lastLoginSignin?.deviceType === DeviceType.MOBILE && <DevicePhoneMobileIcon />}
            {user.signin.lastLoginSignin?.deviceType === DeviceType.TABLET && <DeviceTabletIcon />}
          </span>
        </span>
      )}

      <span className="flex flex-col text-xs">
        <span className="uppercase">
          <b>{convertTimeAgo(convertDate(user.signin.lastLoginSignin.created.date))}</b>
        </span>
        <span>
          {user.signin.lastLoginSignin?.browser} {user.signin.lastLoginSignin?.os}
        </span>
        <span>
          {user.signin.lastLoginSignin?.city} {user.signin.lastLoginSignin?.countryCode}
        </span>
      </span>
    </div>
  );
}

function Select({ courseProgress }: { courseProgress }) {
  const dispatch = useAppDispatch();

  const selected = useAppSelector(state =>
    state.core.report.reports[ReportType.COURSE_PROGRESS]
      ? state.core.report.reports[ReportType.COURSE_PROGRESS]?.selected[courseProgress.id]
      : null,
  );

  const onClick = useCallback(
    e => {
      e.stopPropagation();
      //dispatch(setModal({ id: courseProgress.id, type: Modals.DELETE, entityType: EntityTypes.COURSE_PROGRESS }))
      //dispatch(update({ type: EntityTypes.COURSE_PROGRESS, entity: { id: courseProgress.id, status: -1 } }));
      dispatch(selectReportRow({ type: ReportType.COURSE_PROGRESS, id: courseProgress.id, selected: !selected }));
    },
    [courseProgress.id, dispatch, selected],
  );

  return (
    <div
      onClick={onClick}
      className="w-5 h-5 border-gray-700 border rounded cursor-pointer justify-center items-center flex"
    >
      <CheckIcon className={`w-3 h-3 ${selected ? '' : 'hidden'}`} />
    </div>
  );
}
